import { useState } from 'react';

type TypeUseDownloadReadyOptions<T> = {
  /** 초기 필터 값 */
  initialFilter: T;
  /** 필터 비교를 위한 커스텀 함수 */
  compareFilter?: (current: T, searched: T) => boolean;
};

/**
 * 다운로드 준비 상태 관리 훅
 * - 리스트 검색 완료 후 다운로드 가능 상태 설정
 * - 필터 변경 감지하여 다운로드 가능 상태 초기화
 */
const useDownloadReady = <T, P = unknown>(options: TypeUseDownloadReadyOptions<T>) => {
  const { initialFilter, compareFilter } = options;

  const [isDownloadReady, setIsDownloadReady] = useState(false);
  const [searchedFilter, setSearchedFilter] = useState<T>(initialFilter);
  const [searchedParam, setSearchedParam] = useState<P | null>(null);

  // 검색 완료 후 다운로드 가능 상태 설정
  const markDownloadReady = ({
    filterValue,
    hasData,
    searchedParam,
  }: {
    filterValue: T;
    hasData: boolean;
    searchedParam?: P;
  }) => {
    setSearchedFilter(filterValue);
    setIsDownloadReady(hasData);
    setSearchedParam(searchedParam ?? null);
  };

  // 필터 변경 감지하여 다운로드 가능 상태 초기화
  const checkFilterChange = (currentFilter: T) => {
    const hasChanged = compareFilter ? !compareFilter(currentFilter, searchedFilter) : currentFilter !== searchedFilter;

    if (isDownloadReady && hasChanged) {
      setIsDownloadReady(false);
      setSearchedParam(null);
    }
  };

  // 다운로드 가능 상태 초기화
  const resetDownloadState = () => {
    setIsDownloadReady(false);
    setSearchedFilter(initialFilter);
    setSearchedParam(null);
  };

  return {
    isDownloadReady,
    searchedFilter,
    searchedParam,
    markDownloadReady,
    checkFilterChange,
    resetDownloadState,
  };
};

export default useDownloadReady;
